import { formatOptions, type FormatOptions, type Locale } from "@mindshift/cake-mindshift-next";
import { useHomepage } from "@reco/shared/src/composables/useHomepage";
import type { LocaleContext } from "@reco/shared/src/composables/useLocale";
import { GridBoxType, Scenario } from "@reco/shared/src/constants";
import type { GridBox } from "@reco/shared/src/types/gridbox";
import type { Recommendation, RecommendationsResult } from "@reco/shared/src/types/recommendation";
import { type App, type Component, createApp } from "vue";
import { useI18n } from "vue-i18n";
import i18n from "./i18n";
import { sharedPinia } from "./stores";

import {
  getGridboxType,
  isShopTheLookResult,
  isShopTheLookScenario,
  isStandardRecommendationsResult,
} from "@reco/service/src/utils/helpers";
import BundlesWidget from "./components/Bundles/BundlesWidget.vue";
import ListWidget from "./components/List/ListWidget.vue";
import RecoSliderWidget from "./components/Slider/RecoSliderWidget.vue";
import STLSliderWidget from "./components/Slider/STLSliderWidget.vue";
import { MIN_STL_ITEMS } from "./composables/useLoadSTLFragments";
/**
 * Composable that returns all unique gs1Attributes labels from the recommendations
 * @param recommendations - The recommendations to get the gs1Attributes labels from
 * @param locale - The locale to use for the labels
 * @returns The gs1Attributes labels
 */
export const useAddGs1AttributeLabels = (recommendations: GridBox[], locale: string) => {
  const { t } = useI18n();
  const labels: string[] = [];

  recommendations.forEach((recommendation) => {
    recommendation.gs1Attributes.forEach((attr) => {
      if (!labels.includes(attr.keyLabel as string)) {
        labels.push(attr.keyLabel as string);
      }
    });
  });

  if (!labels.length) {
    return [];
  }

  const preDefinedOrder = [
    t(locale, "reco-frontend-next.Comparisons.labels.type_of_material"),
    t(locale, "reco-frontend-next.Comparisons.labels.weatherproof"),
  ];

  labels.sort((a, b) => {
    if (preDefinedOrder.indexOf(a) === -1) return 1;
    if (preDefinedOrder.indexOf(b) === -1) return -1;

    return preDefinedOrder.indexOf(a) - preDefinedOrder.indexOf(b);
  });

  return labels;
};

export function normalizeLocale(locale: string) {
  return locale.replaceAll("_", "-");
}

/**
 * Get the price format for the given locale, which can then be passed to the MPrice component from @mindshift/cake-mindshift-next
 * @param locale - The locale to get the price format for
 * @returns The price format for the given locale
 */
export const getPriceFormatForLocale = (locale: Locale): FormatOptions | undefined => {
  const fmtMap = formatOptions as Record<string, FormatOptions>;
  const priceFormat = fmtMap[locale] ?? undefined;

  return priceFormat;
};

/**
 * Determines the bundle tracking list based on the relevance score of a product.
 * - "manual" for relevance >= 2.0 (usually 2.0, 3.0, 4.0)
 * - "automatic" for relevance < 2.0
 * Appends the bundle type to the original tracking list name.
 *
 * @param recommendationItems - The list of recommendation items from the API.
 * @param product - The product to get the tracking list for.
 * @returns The adjusted tracking list name (e.g., "reco_pdp_bundles_manual").
 */
export function getAdjustedBundleTrackingList(recommendationItems: Recommendation[], product: Recommendation): string {
  const originalList = product.list;
  const item = recommendationItems.find((item) => item.itemId === product.erpNumber);

  if (!item?.relevance) {
    return `${originalList}_automatic`;
  }

  const bundleType = item.relevance >= 2.0 ? "manual" : "automatic";
  return `${originalList}_${bundleType}`;
}

/**
 * Calculates the appropriate slider item size based on the current page and recommendation.
 * @param recommendation The recommendation to determine the slider item size for
 * @returns The slider item size, either "small" or "medium"
 */
export function getSliderItemSize(recommendation?: Recommendation): "small" | "medium" {
  const { isHomepage } = useHomepage();
  const isLastSeen = recommendation?.list?.includes("last_seen");

  if (isHomepage.value && isLastSeen) {
    return "small";
  }

  return "medium";
}

/**
 * Converts a string value to a boolean or undefined for the showTitle property.
 * @param value The string value to convert
 * @returns true if "true", false if "false", otherwise undefined
 */
export const getShowTitleProperty = (value?: string): boolean | undefined => {
  const showTitleStr = value?.trim().toLowerCase();

  if (showTitleStr === "true") return true;
  if (showTitleStr === "false") return false;

  return undefined;
};

/**
 * Retrieves the ERP number from the URL query parameters.
 * @returns The ERP number as string if available, else null
 */
export const getErpNumber = (): string | null => {
  const params = new URLSearchParams(document.location.search);
  return params.get("erpNumber");
};

/**
 * Retrieves a list of all HTML Elements with the data-recommendations attribute to mount reco widgets to.
 * @returns List of HTML Elements to mount reco widgets to
 */
export const getMountElements = () => {
  const elements = document.querySelectorAll("[data-recommendations]") as NodeListOf<HTMLElement>;
  return Array.from(elements).filter((elem) => elem.dataset.type && !(elem as ElementWithVueApp).__vue_app__); // skip re-mounting (bugfix for all elements disappearing when opening the card overlay)
};

/**
 * Creates the props for a Reco widget based on the service response and the target HTML element.
 * @param response The response from the Reco service for that widget
 * @param elem The data-recommendations element to mount the Reco widget to
 * @param localeContext The locale context including locale, country, and language
 * @returns The props to be passed to the Reco widget
 */
export const createWidgetOptions = (
  response: RecommendationsResult,
  elem: HTMLElement,
  localeContext: LocaleContext,
) => {
  const baseOptions = {
    heading: elem.dataset.heading,
    subHeading: elem.dataset?.subheading,
    headingElement: elem.dataset?.headingElement,
    showTitle: getShowTitleProperty(elem.dataset.showTitle),
    abTest: response.abTest,
    locale: localeContext.locale,
    countryId: localeContext.country,
    languageId: localeContext.language,
    colorScheme: response.colorScheme,
    creativeSlot: elem.dataset.creativeSlot,
    creativeSlotReco: elem.dataset.creativeSlotReco,
    scenario: response.scenario,
  };

  if (isShopTheLookResult(response)) {
    return {
      ...baseOptions,
      shopTheLookItems: response.shopTheLookItems,
    };
  }

  return {
    ...baseOptions,
    recommendations: response.recommendations,
    recommendationItems: response.recommendationItems,
    brand: response.brandName,
    category: response.categoryPathName,
    categoryName: response.categoryName,
  };
};

type WidgetOptions = ReturnType<typeof createWidgetOptions>;

export interface ElementWithVueApp extends Element {
  __vue_app__?: App;
}

/**
 * Mounts a Reco widget to a given HTML element with the provided props.
 * Also connects the shared Pinia store and i18n instance to the widget.
 * @param comp The Reco widget to mount
 * @param elem The data-recommendations element to mount the Reco widget to
 * @param props The props to pass to the Reco widget
 */
export const mountWidget = (comp: Component, elem: Element, props: Partial<WidgetOptions>): void => {
  const app = createApp(comp, props);
  const existingApp = (elem as ElementWithVueApp).__vue_app__;
  if (existingApp) {
    existingApp.unmount();
  }
  app.use(sharedPinia).use(i18n).mount(elem);
};

/**
 * Checks if we are not in dev mode and not in storybook
 * @returns True if we are not in dev mode and not in storybook
 */
export const shouldCheckOSCountry = (): boolean => {
  return !import.meta.env.DEV && !import.meta.env.STORYBOOK;
};

/**
 * Determines if the widget should be rendered as a list based on the country and recommendation list type.
 * It is supposed to be rendered as a list if the gridbox type is HORIZONTAL_REDUCED.
 * @param countryId The country ID
 * @param recommendationList The recommendation list type
 * @returns True if the widget should be rendered as a list, false otherwise
 */
const isListWidget = (countryId: string, recommendationList: string): boolean => {
  return getGridboxType(countryId, recommendationList) === GridBoxType.HORIZONTAL_REDUCED;
};

/**
 * Mounts the appropriate Reco widget based on the scenario and recommendations received.
 * Returns early without mounting if there are insufficient recommendations for certain scenarios.
 * @param response The Reco service response
 * @param elem The HTML element to mount the widget to
 * @param options The props to pass to the widget
 */
export const mountWidgetBasedOnScenario = (
  response: RecommendationsResult,
  elem: HTMLElement,
  options: WidgetOptions,
): void => {
  const scenario = response.scenario;

  if (isShopTheLookScenario(scenario)) {
    if (isStandardRecommendationsResult(response)) {
      console.warn(
        "[Reco ShopTheLook]: Not mounting widget: received standard recommendations result instead of STL result.",
      );
      return;
    }
    const shopTheLookItems = response.shopTheLookItems;
    if (!shopTheLookItems?.length || shopTheLookItems.length < MIN_STL_ITEMS) {
      console.warn("[Reco ShopTheLook]: Not mounting widget: less than 4 shopTheLookItems received.");
      return;
    }
    mountWidget(STLSliderWidget, elem, options);
    return;
  } else if (!isStandardRecommendationsResult(response)) {
    console.warn(
      `[Reco Widgets]: Not mounting widget: received ShopTheLook result instead of standard recommendations result for scenario ${scenario}.`,
    );
    return;
  }

  const recos = response.recommendations ?? [];
  const numberOfRecos = recos.length;
  const isLastSeenSlider = scenario === Scenario.LAST_SEEN;

  if (isLastSeenSlider && numberOfRecos === 0) {
    return;
  }

  if (numberOfRecos < 2 && !isLastSeenSlider) {
    console.warn(`[Reco Widgets] Not mounting scenario: ${scenario}. Needs at least 2 recos, found ${numberOfRecos}.`);
    return;
  }

  if (scenario == Scenario.BUNDLES) {
    mountWidget(BundlesWidget, elem, options);
  } else if (scenario == Scenario.COMPARISONS) {
    return;
  } else if (isListWidget(options.countryId, response.recommendations[0]?.list || "")) {
    mountWidget(ListWidget, elem, options);
  } else {
    mountWidget(RecoSliderWidget, elem, options);
  }
};
